import type { PreloadedQuery } from 'react-relay';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { getTopNavRelayVariables } from '@jira/navigation-apps__resources/src/services/top-nav/index.tsx';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import {
	buildNavigationAriForMenuData,
	buildNavigationAriForSidebarData,
} from '@atlassian/global-side-navigation/navigation-ari';
import type { RouteResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import queryNode, {
	type useJiraAppChromeQuery as useJiraAppChromeQueryType,
} from './__generated__/useJiraAppChromeQuery.graphql.ts';

const RESOURCE_TYPE_APP_CHROME = 'RESOURCE_TYPE_APP_CHROME';

export const appChromeResource: RouteResource<
	PreloadedQuery<useJiraAppChromeQueryType, Record<string, unknown>>
> = createRelayResource<useJiraAppChromeQueryType>({
	type: RESOURCE_TYPE_APP_CHROME,
	// Anonymous users and chromeless routes must not trigger this GraphQL query.
	// Use the resource-level `skip` predicate so `loadQuery` is never invoked for
	// them — `getQuery` will not run when `skip` returns `true`.
	skip: (routerContext, { tenantContext: { isAnonymous } }) => {
		return shouldSkipAppChromeQuery({
			isAnonymous,
			// Chromeless routes render no top nav and no side nav, so this
			// `isCritical` query would be SSR-blocking work whose data is never
			// read. `route.layout` is the same plain-object layout config that
			// drives the renderer's `useIsChromeless()`, so the two cannot disagree.
			isChromeless: routerContext.route.layout?.isChromeless === true,
		});
	},
	getQuery: (routerContext, { tenantContext }) => {
		const { activationId, atlassianAccountId, cloudId, isAnonymous } = tenantContext;
		const ownerAri =
			atlassianAccountId && IdentityUserAri.create({ userId: atlassianAccountId }).toString();
		return {
			parameters: queryNode,
			variables: {
				// The OLD side-nav settings record, read only while the side nav runs
				// `customizationStore="network-migration"`. Derived exactly as
				// `useNetworkStorageParams` derives `entityAri`, so the migration reads the
				// record the side nav is migrating away from.
				entityAriUserSideNavSettings: JiraWorkspaceAri.create({
					siteId: cloudId,
					activationId,
				}).toString(),
				// The old record is per-user, so there is nothing to migrate without an owner.
				fetchOldEntityRecord: Boolean(ownerAri),
				// The side nav's menu-customization record. Derived the same way as
				// `navPlatSidebarDataEntityAri` so the query reads the same record the
				// navigation components write to.
				navPlatMenuDataEntityAri: buildNavigationAriForMenuData({
					activationId,
					firstPartyApp: 'jira',
					siteId: cloudId,
				}),
				navPlatSidebarDataEntityAri: buildNavigationAriForSidebarData({
					activationId,
					firstPartyApp: 'jira',
					siteId: cloudId,
				}),
				ownerAri: ownerAri || null,
				skipNavPlatSidebarData: isAnonymous,
				fetchGlobalShortcutsAvailableProducts: Boolean(
					cloudId && ownerAri && fg('navx-4515-jira-gsn-gas-ssr'),
				),
				// The GlobalRoot query also spreads `...TopNavigation_slotsQuery`, whose
				// child fragments read these as root operation variables.
				...getTopNavRelayVariables(routerContext, tenantContext),
			},
			options: { fetchPolicy: 'store-or-network' },
		};
	},
	isCritical: true,
});

/**
 * Whether the app-chrome query should be skipped for this render.
 */
function shouldSkipAppChromeQuery({
	isAnonymous,
	isChromeless,
}: {
	isAnonymous: boolean;
	isChromeless: boolean;
}): boolean {
	return isAnonymous || isChromeless;
}
